/**
 * Image validation utilities for rich text editor
 */

export const IMAGE_VALIDATION = {
  ALLOWED_TYPES: ['image/png', 'image/gif', 'image/jpeg', 'image/jpg', 'image/webp'],
  MAX_SIZE_BYTES: 10 * 1024 * 1024, // 10MB
  MAX_DIMENSION_PIXELS: 4096, // 4K resolution limit
};

/**
 * Validates an image file for type and size
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error?: string }
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file selected.' };
  }

  // File type validation
  if (!IMAGE_VALIDATION.ALLOWED_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Please select a PNG, GIF, JPEG, or WebP image.`
    };
  }

  // File size validation
  if (file.size > IMAGE_VALIDATION.MAX_SIZE_BYTES) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    const maxSizeMB = (IMAGE_VALIDATION.MAX_SIZE_BYTES / 1024 / 1024).toFixed(0);
    return {
      isValid: false,
      error: `File too large. Please select an image smaller than ${maxSizeMB}MB. Current size: ${fileSizeMB}MB`
    };
  }

  return { isValid: true };
};

/**
 * Validates image dimensions after loading
 * @param {string} dataUrl - The image data URL
 * @returns {Promise<Object>} - Promise resolving to { isValid: boolean, error?: string, dimensions?: { width: number, height: number } }
 */
export const validateImageDimensions = (dataUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      const dimensions = { width: img.width, height: img.height };
      
      if (img.width > IMAGE_VALIDATION.MAX_DIMENSION_PIXELS || img.height > IMAGE_VALIDATION.MAX_DIMENSION_PIXELS) {
        resolve({
          isValid: false,
          error: `Image dimensions too large. Maximum allowed: ${IMAGE_VALIDATION.MAX_DIMENSION_PIXELS}x${IMAGE_VALIDATION.MAX_DIMENSION_PIXELS} pixels. Current: ${img.width}x${img.height} pixels.`,
          dimensions
        });
      } else {
        resolve({ isValid: true, dimensions });
      }
    };

    img.onerror = () => {
      resolve({
        isValid: false,
        error: 'Unable to load image. The file may be corrupted.'
      });
    };

    img.src = dataUrl;
  });
};

/**
 * Handles the complete image selection and validation process
 * @param {Event} event - The file input change event
 * @param {Function} setState - React setState function
 * @param {Function} onSuccess - Callback when validation passes, receives (file, dataUrl)
 * @returns {Promise<void>}
 */
export const handleImageSelection = async (event, setState, onSuccess) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Basic file validation
  const fileValidation = validateImageFile(file);
  if (!fileValidation.isValid) {
    setState((prevState) => ({
      ...prevState,
      feedbackMessage: fileValidation.error,
      feedbackVariant: 'error',
    }));
    return;
  }

  // Read file as data URL
  const reader = new FileReader();
  
  reader.onloadend = async () => {
    try {
      // Validate dimensions
      const dimensionValidation = await validateImageDimensions(reader.result);
      if (!dimensionValidation.isValid) {
        setState((prevState) => ({
          ...prevState,
          feedbackMessage: dimensionValidation.error,
          feedbackVariant: 'error',
        }));
        return;
      }

      // All validations passed, call success callback
      onSuccess(file, reader.result);
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        feedbackMessage: 'Failed to process the selected image. Please try again.',
        feedbackVariant: 'error',
      }));
    }
  };

  reader.onerror = () => {
    setState((prevState) => ({
      ...prevState,
      feedbackMessage: 'Failed to read the selected file. Please try again.',
      feedbackVariant: 'error',
    }));
  };

  reader.readAsDataURL(file);
};